// src/onboarding/activation/Day1TorroExperience.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createChart, CandlestickSeries } from "lightweight-charts";
import confetti from "canvas-confetti";
import { day1BtcUsdOhlc } from "../../data/day1_btcusd_ohlc";

// helpers
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const VISIBLE_COUNT = 22;
const OUTCOME_COUNT = 8;

export default function Day1TorroExperience() {
  const navigate = useNavigate();

  // flow phases: intro | aiQuestion | flashing | awaiting | feedback | reveal | reward
  const [phase, setPhase] = useState("intro");
  const [torroStage, setTorroStage] = useState("baby"); // baby | young

  // economy/progress
  const [tokens, setTokens] = useState(100);
  const [xp, setXp] = useState(0);
  const [growth, setGrowth] = useState(0);
  const [badge, setBadge] = useState(null);

  // AI prompt
  const [prompt, setPrompt] = useState("");
  const [aiReply, setAiReply] = useState("");

  // decision state
  const [choice, setChoice] = useState(null);
  const [choiceState, setChoiceState] = useState(null); // 'correct' | 'incorrect' | null
  const [correct, setCorrect] = useState(null);
  const [showChoices, setShowChoices] = useState(false);
  const [learnLabel, setLearnLabel] = useState("");

  // chart refs
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const disposedRef = useRef(false);
  const roRef = useRef(null);

  // slices
  const visibleBars = day1BtcUsdOhlc.slice(0, VISIBLE_COUNT);
  const outcomeBars = day1BtcUsdOhlc.slice(VISIBLE_COUNT, VISIBLE_COUNT + OUTCOME_COUNT);

  // hydrate progress (persisted)
  useEffect(() => {
    const s = localStorage.getItem("ft_day1_state");
    if (!s) return;
    try {
      const { tokens: t, xp: x, growth: g, torroStage: stage, badge: b } = JSON.parse(s);
      if (t != null) setTokens(t);
      if (x != null) setXp(x);
      if (g != null) setGrowth(g);
      if (stage) setTorroStage(stage);
      if (b) setBadge(b);
    } catch {}
  }, []);

  // save on reward
  useEffect(() => {
    if (phase !== "reward") return;
    localStorage.setItem(
      "ft_day1_state",
      JSON.stringify({ tokens, xp, growth, torroStage, badge })
    );
  }, [phase, tokens, xp, growth, torroStage, badge]);

  // chart phases helper
  const isChartPhase = (p) => ["flashing", "awaiting", "feedback", "reveal"].includes(p);

  // create the chart when we enter a chart phase
  useEffect(() => {
    if (!isChartPhase(phase)) return;
    if (!containerRef.current) return;

    disposedRef.current = false;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 280,
      layout: { background: { color: "#0E0F13" }, textColor: "#DDE2EA" },
      grid: { vertLines: { color: "#1C2128" }, horzLines: { color: "#1C2128" } },
      timeScale: { timeVisible: true, borderVisible: false, rightOffset: 2 },
      rightPriceScale: { borderVisible: false },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: "#2ECC71",
      downColor: "#FF4C4C",
      borderVisible: false,
      wickUpColor: "#2ECC71",
      wickDownColor: "#FF4C4C",
    });

    series.setData(visibleBars);
    chart.timeScale().fitContent();

    chartRef.current = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      if (disposedRef.current) return;
      chart.applyOptions({ width: containerRef.current?.clientWidth || 320 });
    });
    ro.observe(containerRef.current);
    roRef.current = ro;

    return () => {
      disposedRef.current = true;
      try { ro.disconnect(); } catch {}
      try { chart.remove(); } catch {}
      chartRef.current = null;
      seriesRef.current = null;
      roRef.current = null;
    };
  }, [phase, visibleBars]);

  // flashing → awaiting with delayed choices
  useEffect(() => {
    let alive = true;
    (async () => {
      if (phase !== "flashing") return;
      setShowChoices(false);
      await wait(700);
      if (!alive) return;
      setPhase("awaiting");
      await wait(300);
      if (!alive) return;
      setShowChoices(true);
    })();
    return () => { alive = false; };
  }, [phase]);

  // intro → ask AI
  const startDay1 = () => setPhase("aiQuestion");

  const handleAskAI = async (e) => {
    e.preventDefault();
    const firstIsFree = aiReply === "";
    if (!firstIsFree) setTokens((t) => Math.max(0, t - 1)); // later messages cost 1

    // Swap with real AI call
    const reply =
      "Great Q! Watch momentum tells like strong closes and rejection wicks. Let’s test it now.";
    setAiReply(reply);
    setGrowth((g) => Math.min(100, g + 8));

    await wait(500);
    setPhase("flashing");
  };

  // decision flow
  const handleDecision = async (key) => {
    if (phase !== "awaiting") return;
    setChoice(key);
    const isCorrect = key === "A"; // dataset: continuation-up is best
    setCorrect(isCorrect);
    setChoiceState(isCorrect ? "correct" : "incorrect");
    if (!prefersReducedMotion) navigator.vibrate?.(isCorrect ? 25 : 60);

    setPhase("feedback");
    await wait(500);
    setPhase("reveal");

    // reveal outcome bars
    if (seriesRef.current && !disposedRef.current) {
      let cur = [...visibleBars];
      for (let i = 0; i < outcomeBars.length; i++) {
        if (disposedRef.current) return;
        cur = [...cur, outcomeBars[i]];
        seriesRef.current.setData(cur);
        await wait(prefersReducedMotion ? 0 : 120);
      }
    }
    setLearnLabel(isCorrect ? "Continuation after higher low" : "Rejection → pullback signal");

    // reward + evolve
    setPhase("reward");
    setXp((x) => x + (isCorrect ? 50 : 35));
    setTokens((t) => t + (isCorrect ? 50 : 40));
    setBadge("Chart Rookie");
    setGrowth((g) => Math.min(100, g + (isCorrect ? 35 : 25)));
    await wait(350);
    setTorroStage("young");
    setTimeout(() => setChoiceState(null), 800);
  };

  const resetMove = () => {
    setChoice(null);
    setCorrect(null);
    setPhase("awaiting");
    setLearnLabel("");
    if (seriesRef.current && !disposedRef.current) {
      seriesRef.current.setData(visibleBars);
      chartRef.current?.timeScale().fitContent();
    }
    setShowChoices(true);
  };

  // confetti on reward
  useEffect(() => {
    if (phase !== "reward" || prefersReducedMotion) return;
    confetti({ particleCount: 90, spread: 60, origin: { y: 0.3 } });
  }, [phase]);

  const handleArena = () => {
    if (tokens < 2) return alert("Not enough tokens. Play another move to earn some!");
    setTokens((t) => t - 2);
    navigate("/arena/chart-patterns?entry=2");
  };

  return (
    <div className="day1">
      {/* Top bar */}
      <div className="topbar">
        <div className="brand">🐂 FinTorro</div>
        <div className="meta">
          <span className="pill">🪙 Tokens: {tokens}</span>
          <span className="pill">✨ XP: {xp}</span>
        </div>
      </div>

      {/* INTRO */}
      {phase === "intro" && (
        <section className="card intro">
          <div className="torro-card">
            <div className={`torro-emoji ${torroStage === "baby" ? "bounce" : ""}`}>🐂</div>
            <div className="stage">Baby Torro</div>
            <div className="growth"><div className="growth-bar" style={{ width: `${growth}%` }} /></div>
          </div>
          <h1 className="title">Torro Day 1</h1>
          <p className="lead">“I’ll help you master the markets — and together we’ll grow stronger every day!”</p>
          <p className="sub">Torro grows when you learn & practice.</p>
          <button className="btn-primary" onClick={startDay1}>Ask Torro your first trading question</button>
        </section>
      )}

      {/* AI QUESTION */}
      {phase === "aiQuestion" && (
        <section className="card ai">
          <div className="torro-mini">
            <span className="emoji">🐂</span>
            <span className="label">Baby Torro</span>
          </div>
          <form className="ask" onSubmit={handleAskAI}>
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., How do I spot a fake breakout?"
            />
            <button className="btn-primary" type="submit">Ask Torro (first is free)</button>
          </form>
          {aiReply && <div className="bubble">🤖 {aiReply}</div>}
          <div className="hint">After your question, we’ll run a 60‑second mini‑test.</div>
        </section>
      )}

      {/* CHART MOVE */}
      {(phase === "flashing" || phase === "awaiting" || phase === "feedback" || phase === "reveal") && (
        <section className="card move">
          <h2 className="title2">Chart Flash Reveal</h2>
          <div ref={containerRef} className={`chart ${phase === "flashing" ? "flash" : ""}`} />
          {learnLabel && phase !== "awaiting" && <div className="learn-tag">📌 {learnLabel}</div>}

          {phase === "awaiting" && (
            <>
              <p className={`prompt ${showChoices ? "in" : ""}`}>What’s most likely next here?</p>
              <div className="choices" role="group" aria-label="Choose market outcome">
                <button
                  className={`choice ${choice === "A" ? "active" : ""} ${
                    choice === "A" && choiceState === "correct" ? "ok" : ""
                  } ${choice === "A" && choiceState === "incorrect" ? "no" : ""}`}
                  onClick={() => handleDecision("A")}
                >
                  A) Continuation up
                </button>
                <button
                  className={`choice ${choice === "B" ? "active" : ""} ${
                    choice === "B" && choiceState === "correct" ? "ok" : ""
                  } ${choice === "B" && choiceState === "incorrect" ? "no" : ""}`}
                  onClick={() => handleDecision("B")}
                >
                  B) Pullback to support
                </button>
                <button
                  className={`choice ${choice === "C" ? "active" : ""} ${
                    choice === "C" && choiceState === "correct" ? "ok" : ""
                  } ${choice === "C" && choiceState === "incorrect" ? "no" : ""}`}
                  onClick={() => handleDecision("C")}
                >
                  C) Sharp reversal
                </button>
              </div>
            </>
          )}
          {phase === "feedback" && (
            <div className={`coach ${correct ? "good" : "bad"}`}>
              {correct
                ? "🎯 Nice read! Momentum + strong close hinted at continuation."
                : "🧠 Classic fake‑out near resistance. Watch the rejection wick & weak follow‑through."}
            </div>
          )}
        </section>
      )}

      {/* REWARD */}
      {phase === "reward" && (
        <section className="card reward">
          <div className="torro-card evolved">
            <div className="torro-emoji celebrate">🐂</div>
            <div className="stage">Young Torro</div>
            <div className="growth"><div className="growth-bar" style={{ width: `${growth}%` }} /></div>
          </div>
          <h2 className="title2">🎉 Great job!</h2>
          <ul className="rewards">
            <li>✨ +50 XP</li>
            <li>🪙 +50 tokens</li>
            <li>🏅 Badge unlocked: <b>{badge}</b></li>
          </ul>
          <div className="cta-row">
            <button className="btn-primary" onClick={handleArena}>🔥 2‑min Chart Arena (Cost: 2 tokens)</button>
            <button className="btn-secondary" onClick={resetMove}>Replay the move</button>
          </div>
          <p className="foot">AI can make mistakes. Use with discretion.</p>
        </section>
      )}

      {/* Optional tiny phase badge for QA */}
      {/* <div style={{position:'fixed', right:8, bottom:8, fontSize:12, opacity:.6}}>phase: {phase}</div> */}

      {/* styles */}
      <style>{`
        .day1 { background:#0B0D11; color:#DDE2EA; min-height:100vh; padding:16px; }
        .topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
        .brand { font-weight:800; }
        .meta { display:flex; gap:8px; }
        .pill { background:#12151B; border:1px solid #1E232B; padding:6px 10px; border-radius:999px; font-size:13px; }

        .card { max-width:960px; margin:20px auto; background:#0F141B; border:1px solid #1E2630; border-radius:16px; padding:16px; text-align:center; }
        .intro .title { color:#FF7C24; margin:6px 0; }
        .lead { font-size:16px; margin:6px 0 2px; }
        .sub { color:#9FB0C5; margin:0 0 12px; }

        .btn-primary { background:#FF7C24; color:#000; border:none; border-radius:12px; padding:10px 14px; font-weight:800; cursor:pointer; }
        .btn-secondary { background:#131B24; color:#DDE2EA; border:1px solid #253041; border-radius:12px; padding:10px 14px; }
        .cta-row { display:flex; gap:8px; justify-content:center; margin-top:12px; }

        .torro-card { width:140px; margin:0 auto 8px; background:#10141A; border:1px solid #1F2630; border-radius:16px; padding:10px; }
        .torro-card.evolved { box-shadow:0 0 24px rgba(255,124,36,0.25); }
        .torro-emoji { font-size:40px; }
        .torro-emoji.bounce { animation: bounce 1.3s infinite; }
        .torro-emoji.celebrate { animation: jump 0.7s ease-out 0s 2; }
        .stage { font-size:12px; color:#A9B4C3; margin-top:4px; }
        .growth { height:8px; background:#121821; border-radius:999px; margin-top:8px; overflow:hidden; }
        .growth-bar { height:100%; background:#FF7C24; transition: width 400ms ease; }

        .ai .ask { display:flex; gap:8px; justify-content:center; margin-top:8px; flex-wrap:wrap; }
        .ai input { width:360px; max-width:80vw; padding:10px 12px; border-radius:10px; border:1px solid #2A3444; background:#0D1218; color:#DDE2EA; }
        .bubble { margin:10px auto 4px; background:#0E1520; border:1px solid #203046; padding:10px; border-radius:12px; max-width:680px; }
        .hint { color:#9FB0C5; font-size:12px; }

        .move .title2, .reward .title2 { color:#FF7C24; margin:4px 0 8px; }
        .chart { height:280px; border-radius:16px; overflow:hidden; }
        .chart.flash { animation: flashGlow 1s ease-out; }
        .learn-tag { margin:8px auto 0; display:inline-block; padding:6px 10px; background:#0E1520; border:1px solid #203046; border-radius:10px; font-size:12px; color:#CFE0FF; }
        .prompt { margin:10px 0 6px; opacity:0; transform:translateY(4px); transition: opacity .25s ease, transform .25s ease; }
        .prompt.in { opacity:1; transform:translateY(0); }

        .choices { display:grid; grid-template-columns:1fr; gap:8px; max-width:680px; margin:0 auto; }
        @media (min-width: 640px){ .choices{ grid-template-columns: repeat(3, 1fr); } }
        .choice { background:#11151B; border:1px solid #1E232B; color:#DDE2EA; border-radius:12px; padding:12px; cursor:pointer; transition: transform 120ms ease, border-color 120ms }
        .choice:hover { border-color:#2A3240; transform:translateY(-1px); }
        .choice.ok { border-color:#1DB954; box-shadow:0 0 0 2px rgba(29,185,84,.25) inset; }
        .choice.no { border-color:#E74C3C; animation: shake .25s linear 2; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-2px)} 75%{transform:translateX(2px)} }

        .coach { margin-top:10px; background:#0E1520; border:1px solid #203046; border-radius:12px; padding:10px; }
        .coach.good { border-color:#264B35; }
        .coach.bad { border-color:#4B2A2A; }

        .rewards { list-style:none; padding:0; margin:8px 0 0; display:flex; gap:16px; justify-content:center; flex-wrap:wrap; }
        .foot { color:#8FA4BD; font-size:12px; margin-top:10px; }

        @keyframes flashGlow { 0%{ box-shadow:0 0 0 rgba(255,124,36,0)} 30%{ box-shadow:0 0 24px rgba(255,124,36,0.3)} 100%{ box-shadow:0 0 0 rgba(255,124,36,0)} }
        @keyframes jump { 0%{transform:translateY(0)} 40%{transform:translateY(-10px)} 100%{transform:translateY(0)} }
        @keyframes bounce { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-4px) } }
      `}</style>
    </div>
  );
}
